import { observer } from 'mobx-react-lite'
import FeatureToggleStore, { FeatureToggleStoreProvider, useFeatureToggleStore } from './FeatureToggleStore'
import useStore from 'reusable/mobx-store-utils'
import useInitialMount from 'reusable/hooks/useInitialMount'
import SkeletonLoader from 'reusable/mui/skeleton/SkeletonLoader'
import Switch from "@mui/material/Switch"
import GapDataGrid from "reusable/mui/data-grid/GapDataGrid"
import Button from "@mui/material/Button"
import GapDialog from "reusable/gap-dialog/GapDialog"
import GapAutocompleteMultiple from "features/gap-components/autocomplete/multiple/GapAutocompleteMultiple"
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import GapAutocompleteSingle from 'features/gap-components/autocomplete/single/GapAutocompleteSingle'
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { useCallback, useState } from 'react'
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'
import { messageConfirm } from 'reusable/mui/modals/message-confirm'
import BetaOptInList from './BetaOptInList'

export default observer(function FeatureToggle() {
   const featureToggleStore = useStore(() => new FeatureToggleStore())
   const orgGuardStore = useOrgGuardStore()
   const [confirmed, setConfirmed] = useState(orgGuardStore.isDeveloper)

   useInitialMount(async () => {
      if (!orgGuardStore.isDeveloper) {
         const accepted = await messageConfirm(
            'You are about to enter the feature toggle zone. Changes here affect real users in real time. There is no undo button. Proceed with caution (and coffee).',
            'With great power comes great responsibility',
            { confirmButtonTitle: 'I promise to wield this power responsibly', size: 'small' }
         )
         if (!accepted) {
            window.history.back()
            return
         }
         setConfirmed(true)
      }
      await featureToggleStore.fetch()
   })

   if (!confirmed) return null

   return <SkeletonLoader loading={featureToggleStore.loader.isLoading}>
      <FeatureToggleStoreProvider store={featureToggleStore}>
         <TabContext value={featureToggleStore.activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
               <TabList onChange={featureToggleStore.changeTab}>
                  <Tab label="Toggles" value="toggles" />
                  <Tab label="Beta opt-ins" value="beta-opt-ins" />
               </TabList>
            </Box>
            <TabPanel value="toggles" sx={{ px: 0 }}>
               <GapDataGrid
                  columns={[
                     { field: 'featureToggle', headerName: 'Feature toggle', flex: 1 },
                     { field: 'enabled', headerName: 'Enabled', flex: 1, renderCell: ({ row }) => <Switch checked={row.enabled} data-id={row.id} onClick={featureToggleStore.toggleEnable} /> },
                     { field: 'releasedToBeta', headerName: 'Beta', flex: 1, renderCell: ({ row }) => row.releasedToBeta ? 'Yes' : '—' },
                     {
                        field: 'Setup', headerName: 'Setup', flex: 1, renderCell: ({ row }) => <Button variant="text" data-id={row.id} onClick={featureToggleStore.openSetupModal}>
                           Setup
                        </Button>
                     },
                  ]}
                  rows={featureToggleStore.featureToggles}
                  noRowsText="No feature toggles setup!"
               />
            </TabPanel>
            <TabPanel value="beta-opt-ins" sx={{ px: 0 }}>
               <BetaOptInList />
            </TabPanel>
         </TabContext>
         <FeatureToggleSetupModal />
      </FeatureToggleStoreProvider>
   </SkeletonLoader>
})

const FeatureToggleSetupModal = observer(function FeatureToggleSetupModal() {
   const featureToggleStore = useFeatureToggleStore()

   const renderUserIdInput = useCallback((params: AutocompleteRenderInputParams) => {
      return <TextField
         {...params}
         variant="outlined"
         label="User IDs"
         placeholder="Enter user ID(use enter to add)"
         size="medium"
      />
   }, [])

   return <GapDialog title="Setup feature toggle" modalState={featureToggleStore.setupModal} size="small" primary={{
      name: 'Save',
      callback: featureToggleStore.save,
      disabled: !featureToggleStore.selectedFeatureToggle,
      loading: featureToggleStore.saveLoader.isLoading,
      notCloseAutomatically: true
   }}>
      {featureToggleStore.formHandler && <Grid container spacing={2}>
         <Grid size={12}>
            {featureToggleStore.formHandler.enabledForAllOrgs.bindInput(ip =>
               <FormControlLabel
                  control={<Switch {...ip} />}
                  label="Enable for everyone"
               />
            )}
         </Grid>
         <Grid size={12}>
            <Divider>OR</Divider>
         </Grid>
         <Grid size={12}>
            {featureToggleStore.formHandler.releasedToBeta.bindInput(ip =>
               <FormControlLabel
                  control={<Switch {...ip} />}
                  label="Released to beta"
               />
            )}
            <Typography variant="body2" color="text.secondary">
               Every advisor org with beta opt-in enabled automatically gets this toggle.
            </Typography>
         </Grid>
         <Grid size={12}>
            <Divider>OR</Divider>
         </Grid>
         <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Choose specific users or orgs</Typography>
            <Typography variant="body2" color="text.secondary">
               User IDs, Org IDs, and Orgs Created After use OR logic — matching any one grants access.
               Org Types and Region are AND filters applied on top.
            </Typography>
         </Grid>
         <Grid size={12}>
            <Box sx={{
               pointerEvents: featureToggleStore.selectedFeatureToggle?.enabledForAllOrgs ? 'none' : 'auto',
               opacity: featureToggleStore.selectedFeatureToggle?.enabledForAllOrgs ? 0.5 : 1,
            }}>
               <Stack spacing={3}>
                  {featureToggleStore.formHandler.onlyForOrgTypes.bindInput(ip =>
                     <GapAutocompleteMultiple
                        options={featureToggleStore.orgTypeOptions}
                        label="Org types"
                        {...ip}
                     />
                  )}
                  {featureToggleStore.formHandler.onlyForOrgIds.bindInput(ip =>
                     <GapAutocompleteMultiple
                        options={featureToggleStore.orgIdOptions}
                        label="Org id's"
                        {...ip}
                     />
                  )}
                  {featureToggleStore.formHandler.onlyForRegion.bindInput(ip =>
                     <GapAutocompleteSingle
                        options={featureToggleStore.regionOptions}
                        label="Only for region"
                        {...ip}
                     />
                  )}

                  <Autocomplete
                     multiple
                     id="userid-input"
                     options={[]}
                     value={featureToggleStore.selectedFeatureToggle?.onlyForUserIds || []}
                     freeSolo
                     onChange={featureToggleStore.onUserIdChange}
                     renderInput={renderUserIdInput}
                  />
                  {featureToggleStore.formHandler.onlyForOrgsCreatedAfter.bindInput(ip =>
                     <TextField
                        label="Only for orgs created after"
                        placeholder="e.g. 2026-03-01"
                        {...ip}
                     />
                  )}
               </Stack>
            </Box>
         </Grid>
      </Grid>}
   </GapDialog>
})
